import React, { useState } from "react";

import { useMutation, useQuery } from "@apollo/client";
import {
  Box,
  CircularProgress,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { compact, isEmpty } from "lodash";
import { useParams } from "react-router-dom";
import { makeStyles } from "tss-react/mui";

import { AddIconButton } from "@/components/IconButtons";
import SearchIcon from "@/components/Icons/SearchIcon";
import { graphql } from "@/gql";
import { NoteItemFragment } from "@/gql/graphql";
import useDebounce from "@/hooks/useDebounce";
import { theme } from "@/theme/customTheme";
import { colors } from "@/theme/overrides/colors";
import { useSnackBar } from "@hooks/useSnackBar";

import AddNoteModal from "./AddNoteModal/AddNoteModal";
import NoteItem from "./NoteItem/NoteItem";
import { compareNoteDate } from "./utils/compareNoteDate";

const useStyles = makeStyles()({
  root: {
    position: "relative",
    overflowY: "auto",
    height: "100%",
    padding: theme.spacing(4, 2, 2, 2),
  },
  h4: {
    marginTop: "0px",
    marginBottom: "12px",
  },
  section: {
    width: "100%",
    padding: theme.spacing(0, 4, 4, 4),
    backgroundColor: colors.white,
  },
});

export type FormValues = {
  date: Date | null;
  body: string;
};

export const notesAndObservationQuery = graphql(`
  query NotesAndObservation($id: ID!, $attachedTo: [String!], $text: String) {
    client(id: $id) {
      id
      notes(attachedTo: $attachedTo, text: $text) {
        pageInfo {
          startCursor
          endCursor
          hasNextPage
          hasPreviousPage
        }
        edges {
          cursor
          node {
            ...NoteItem
          }
        }
      }
    }
  }
`);

const deleteNoteMutation = graphql(`
  mutation DeleteNote($input: DeleteNoteInput!) {
    deleteNote(input: $input) {
      note {
        ...NoteItem
      }
    }
  }
`);

const NotesAndObservation: React.FC = () => {
  const { classes } = useStyles();
  const { clientId } = useParams<{ clientId: string }>();
  const [searchString, _setSearchString] = useState<string>("");
  const setSearchString = useDebounce(_setSearchString, 1000);
  const { data, loading, refetch } = useQuery(notesAndObservationQuery, {
    variables: {
      id: clientId ?? "",
      attachedTo: ["profile"],
      text: searchString,
    },
  });
  const edges =
    data?.client?.notes?.edges.map(({ node }) => {
      if (node) return node;
    }) ?? [];
  const notes = compact(edges).sort(compareNoteDate);
  const [SimpleSnackBar, snackBarHelper] = useSnackBar();
  const [modalVisibility, setModalVisibility] = useState<boolean>(false);
  const [deleteNote] = useMutation(deleteNoteMutation);

  const handleModalClose = () => {
    setModalVisibility(false);
  };

  const handleModalOpen = () => {
    setModalVisibility(true);
  };

  const refetchNotes = async () => await refetch();

  const handleDeleteNote = async (note: NoteItemFragment) => {
    try {
      await deleteNote({
        variables: { input: { id: note.id } },
        optimisticResponse: {
          __typename: "Mutation",
          deleteNote: {
            __typename: "DeleteNotePayload",
            note: {
              ...note,
            },
          },
        },
      });
      refetchNotes();
      snackBarHelper.open("Successfully deleted note");
    } catch (e) {
      snackBarHelper.open(
        "There was an error in deleting your note. Please try again later"
      );
    }
  };

  return (
    <div className={classes.root}>
      <section className={classes.section}>
        <Box
          py={3}
          width="100%"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6" fontWeight="bold">
            Notes
          </Typography>
          <AddIconButton onClick={handleModalOpen} />
        </Box>
        <Box width="100%" marginBottom={4} marginTop={1}>
          <TextField
            autoComplete="off"
            onChange={(event) => setSearchString(event.target.value)}
            placeholder="Search..."
            fullWidth
            size="small"
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="inherit" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {isEmpty(notes) && !loading && (
          <Box
            marginTop={2}
            width="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Typography variant="caption">No available notes</Typography>
          </Box>
        )}

        {loading ? (
          <Box
            marginTop={2}
            width="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Grid container item spacing={2} xs={12} alignItems="center">
            {notes?.map((item) => (
              <NoteItem
                key={item.id}
                id={item.id}
                date={item.date}
                text={item.text}
                onDeleteNote={() => handleDeleteNote(item)}
                onUpdateNote={refetchNotes}
              />
            ))}
          </Grid>
        )}

        <AddNoteModal
          visible={modalVisibility}
          onClose={handleModalClose}
          onAddNote={refetchNotes}
        />
        <SimpleSnackBar />
      </section>
    </div>
  );
};

export default NotesAndObservation;
